import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpHeaders,
  HttpErrorResponse
} from '@angular/common/http';

import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { finalize, catchError, filter, take, switchMap, switchMapTo } from 'rxjs/operators';
import { isNull } from 'lodash';

import { environment } from '@env/environment';

import { AuthService } from '@base-shared/services/auth.service';
import { CustomLoadingService } from '@base/shared/services/loading.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private config = environment;
  private queryRegex = new RegExp(/([^\?]+)(\?.*)?/i);
  private tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private refreshingToken = false;

  constructor(
    private authService: AuthService,
    private loading: CustomLoadingService
  ) { }

  /**
   * Catch all request
   * @param req HttpRequest
   * @param next HttpHandler
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.setHeaders(req);

    const _url = req.urlWithParams;
    const notLoading = ['page', 'search', 'searchString'];
    const showLoading: any = notLoading
      .map(key => {
        if (_url.indexOf(key) !== -1)
          return true;
      })
      .filter((key) => key);

    if (!showLoading.length)
      this.loading.show();

    return next.handle(req)
      .pipe(
        finalize(() => this.loading.hide()),
        catchError(error => this.errorHandler(error, req, next))
      );
  }

  /**
   * Add fresh new token to next call
   * @param req HttpRequest
   * @param next HttpHandler
   */
  private addNewToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const headers = {
      'Authorization': `Bearer ${token}`
    };

    // We clone the request, because the original request is immutable
    return req.clone({
      setHeaders: headers
    });
  }

  /**
   * Set request headers
   * @param req HttpRequest
   */
  private setHeaders(req: HttpRequest<any>) {
    let url = req.url;
    const _currentLang = localStorage.getItem('eyrus-lang') || this.config.defaults.lang;
    const _lang = _currentLang === 'es' ? 'es-Es' : _currentLang === 'en' ? 'en-En' : 'es-Es';
    let _contentType = 'application/json';

    // If request already have a defined content type header
    // overwrite the default one with the provided header
    if (req.headers.has('Content-Type'))
      _contentType = req.headers.get('Content-Type');

    const headers = {
      'Accept-Language': _lang,
      'Content-Type': _contentType,
    };

    if (this.config.envName === 'dev'){
      //headers['x-api-key'] = this.config.postmanMockKey;
    }
    

    if (this.authService.token){
      headers['Authorization'] = `Bearer ${this.authService.token}`;
    }

    // When is in debug mode, URL will have mock server params
    if (this.config.debug && url.startsWith(this.config.baseUrl)) {
      let hasParams: any = this.queryRegex.exec(url);
      hasParams = isNull(hasParams) ? [] : hasParams;
      url = hasParams[2] ? `${url}&key=success` : `${url}?key=success`;
    }

    req = req.clone({
      url: url,
      setHeaders: headers
    });

    // this.loading.show();
    return req;
  }

  /**
   * Act like a by pass to know what kind of error we are handling
   * @param error Response error
   * @param req HttpRequest
   * @param next HttpHandler
   */
  private errorHandler(error, req: HttpRequest<any>, next: HttpHandler) {
    if (error instanceof HttpErrorResponse) {
      switch ((<HttpErrorResponse>error).status) {
        case 401:
          return this.handle401Error(error, req, next);

        case 403:
          return this.logoutUser(error);
      }
    }

    return throwError(error);
  }

  /**
   * Logout from app
   * @param error Error data
   */
  private logoutUser(error) {
    this.refreshingToken = false;
    this.authService.logout();
    return throwError(error);
  }

  /**
   * By pass function to avoid typescript types error
   * So this call to unautharizedFlow to handle refresh token
   * @param error Error data
   * @param req HttpRequest
   * @param next HttpHandler
   */
  private handle401Error(error, req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return this.unautharizedFlow(error, req, next);
  }

  /**
   * Handler for not authorized flow
  * @param error Error data
  * @param req HttpRequest
  * @param next HttpHandler
  */
  private unautharizedFlow(error, req: HttpRequest<any>, next: HttpHandler) {
    if (!this.refreshingToken) {
      this.refreshingToken = true;
      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);
      return this.authService.initRefreshToken()
        .pipe(
          switchMap((res: any) => {
            if (!res)
              return this.logoutUser(error);

            this.tokenSubject.next(res);
            this.authService.token = res.jsonWebToken.accessToken;
            this.authService.refreshToken = res.jsonWebToken.refreshToken;
            this.refreshingToken = false;

            req = this.addNewToken(req, res.jsonWebToken.accessToken);
            return next.handle(req);
          }),
          catchError(err => this.logoutUser(error))
        );
    } else {
      return this.tokenSubject
        .pipe(
          filter(token => token != null),
          take(1),
          switchMap((res: any) => {
            req = this.addNewToken(req, res.jsonWebToken.accessToken);
            return next.handle(req);
          })
        );
    }
  }

}
